import { Suspense, useEffect, useState, ChangeEvent, useMemo, useCallback, SyntheticEvent, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';

import usePrevious from 'hooks/usePrevious';

import { getAuditReport, useReportsStore } from 'hooks/useReportsStore';

import { cloneDeep, isEmpty, uniqBy } from 'lodash';
import Loading from 'components/Loading';
import PDFObject from 'lib/components/PDFObject';
import ReportDataTable from './components/ReportDataTable';
import { HoneReportFormats, HoneReportTypes, MENU_ICONS } from '../../../constants';
import { trackError } from 'lib/analytics';
import {
  abilityCan,
  BALANCE_SHEET_PRESETS,
  currencyFormatter2Decimals,
  dismissToast,
  doPost,
  findBSPresetDates,
  FIVE_SECONDS,
  percentageFormatter,
  reportingOrigin,
  reportTitleToUrl,
  showToast,
  sortReportTitles,
  TOAST_ERROR,
  BalanceSheetPreset,
} from 'lib/utils';

import ReportGraph from './components/ReportGraph/ReportGraph';

import toast from 'react-hot-toast';
import { HoneReportTimeframes } from 'domain/models';
import { formatDate } from 'lib/utils';
import { fetchReportById, performReportUpdate, useActiveReportStore } from 'hooks/useActiveReportStore';
import { shallow } from 'zustand/shallow';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocationsStore } from 'hooks/useLocationsStore';

import { useQueryState } from 'hooks/useQueryState';
import { useReportGraphStore } from 'hooks/useReportGraphStore';

import { ReportNotFoundError } from 'domain/errors/report-not-found-error';
import { QueryStatus } from '@tanstack/react-query';
import qs from 'querystring';
import { useHighlightReportSelector } from 'hooks/useHighlightReportSelector';
import ReportDatesModal from 'components/ReportDatesModal';
import classNames from 'classnames';
import ModalCustomDateRange from './ModalCustomDateRange';
import {
  extractReportIds,
  HoneAbilityActions,
  HoneAbilitySubjects,
  ReportColumnTypes,
  ReportConfigurationData,
  ReportRequest,
  ReportTypes,
} from '@hone-automation/common';
import { useAuth, useAuthContext } from 'context/useAuthContext';

import KSHeader from '../../components/KSHeader';
import STBTools from 'presentation/components/SectionToolbar/STBTools';
import { SlidingPane } from 'components/SlidingPane';
import { differenceInSeconds, parseJSON } from 'date-fns';
import { confirmAlert } from 'react-confirm-alert';
import useActiveActions from 'hooks/useActiveActions';
import { getPrintedHeaderDate } from 'lib/reportUtils';
import { Report } from 'lib/honeTableUtils';
import { getHeaderFormattedText } from 'presentation/utils';
import ValidationReportResults from 'components/ValidationReportResults';
import { useGetReportConfiguration } from '../../../hooks';
import { ReportConfiguration } from '@hone-automation/common/lib/Reports';
import { useSendHubspotEmail } from 'hooks/reports/useSendHubspotEmail';
import { ActionProps } from 'presentation/components/SectionToolbar/STBTools/STBTools';
import { IconAudit, IconEmail, IconValidate } from 'components/Icons';
import { useReviewFinancials } from 'hooks/reports/useReviewFInancials';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: (...args: Array<unknown>) => void;
}

function ErrorFallback({ error }: FallbackProps) {
  return (
    <div role="alert">
      <p>This report is being updated, please try again in a few minutes.</p>
      <pre style={{ display: process.env.NODE_ENV === 'development' ? 'block' : 'none' }}>{error.message}</pre>
    </div>
  );
}

interface Props {
  reports: Map<string, HoneReportSummary[]>;
  columnHeaderTitle?: string;
  customHeaders?: string[];
  status: QueryStatus;
  newTable?: boolean;
}

function ReportViewNoPl({
  reports,
  columnHeaderTitle = '',
  customHeaders,
  status,
  newTable = false,
}: Props): JSX.Element {
  const { currentLocationAbilities } = useAuthContext();

  const { isHovered } = useHighlightReportSelector();

  const [showValidationPane, setShowValidationPane] = useState<boolean>(false);

  const [validateReportError] = useState<string | null>(null);

  const [allReportsSelected, setAllReportsSelected] = useQueryState<boolean>('allReports', false);

  const [auditMode, setAuditMode] = useQueryState('audit');
  const [total] = useQueryState('total');
  const [difference] = useQueryState('difference');
  const [validation] = useQueryState('validate');

  const { activeActions } = useActiveActions();
  const showPercentageValues = activeActions.includes('lines');
  const showMoneyValues = activeActions.includes('bars');

  const { currentLocationId, currentLocation } = useLocationsStore(
    state => ({ currentLocationId: state.currentLocationId, currentLocation: state.currentLocation }),
    shallow
  );

  const { data: reportConfiguration } = useGetReportConfiguration(currentLocationId);

  const location = useLocation();
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const { locationId, reportId, reportType } = useParams<{
    locationId: string;
    reportId: string;
    reportType: string;
  }>();

  const originalReportType = reportType;

  const navigateTo = useNavigate();
  const isPLReport = ['income-statement', 'monthly', 'weekly'].includes(reportType!);

  const [chartCollapsed, setChartCollapsed] = useQueryState('chartCollapsed', 'false');

  const isBookkeeper = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  const [, setDateRange] = useQueryState<string>('dateRange');
  const isBalanceSheetPreset = Object.keys(BALANCE_SHEET_PRESETS).includes(reportId!);

  const reportsError = useReportsStore(state => state.error);
  const { selectedReport, setOpenDateRangeModal, setReportsConfiguration } = useReportsStore(
    state => ({
      selectedReport: state.selectedReport,
      setOpenDateRangeModal: state.setOpenDateRangeModal,
      openDateRangeModal: state.openDateRangeModal,
      setReportsConfiguration: state.setReportsConfiguration,
    }),
    shallow
  );

  const isYtd = selectedReport?.timeframe === HoneReportTimeframes.YTDMonthly;

  const {
    validationReportData,
    yearPlotted,
    reportStatus,
    allCategories,
    getGroupReport,
    setYearPlotted,
    setRefreshingReport,
    rangeNoData,
    setStatus,
    currentReport,
    setReportPayload,
  } = useActiveReportStore(
    state => ({
      yearPlotted: state.yearPlotted,
      setRefreshingReport: state.setRefreshingReport,
      setStatus: state.setStatus,
      rangeNoData: state.rangeNoData,
      reportStatus: state.status,
      allCategories: state.allCategories,
      getGroupReport: state.getGroupReport,
      setYearPlotted: state.setYearPlotted,
      currentReport: state.currentReport,
      validationReportData: state.currentReport?.validation,
      setReportPayload: state.setReportPayload,
    }),
    shallow
  );

  const [statusUpdates, setStatusUpdates] = useState<Record<string, string>>({});

  const allReports = reports.get(isPLReport ? 'pl' : reportType!) || [];
  const titles = uniqBy(allReports, 'title');

  const uniqueTitles = sortReportTitles(titles);

  const [titleFilter] = useQueryState<string>('type', reportTitleToUrl(uniqueTitles[0]?.title || ''));
  const [templateId] = useQueryState<string>('templateId');

  const filteredTitleFilter = templateId
    ? reportTitleToUrl(uniqueTitles.find(props => props.templateId === templateId)?.title || '')
    : titleFilter.split('&')[0];

  let filteredReports = allReports;
  if (isPLReport && titleFilter) {
    filteredReports = filteredReports.filter(({ title, timeframe }) => {
      if ((timeframe.includes('YTD') && reportType === 'income-statement') || timeframe.toLowerCase() === reportType) {
        return reportTitleToUrl(title) === filteredTitleFilter;
      }
      return false;
    });
  }

  const prevReportType = usePrevious(reportType);
  const dateRange = urlParams.get('dateRange');

  useEffect(() => {
    rangeNoData && navigateTo(`/app/location/${currentLocationId}/report/${reportType}`, { replace: true });
  }, [rangeNoData]);

  const toggleValidate = () => {
    setShowValidationPane(false);
  };

  const handleValidateReport = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const validate = searchParams.get('validate');
    if (validate) {
      setShowValidationPane(true);
      return;
    }
    searchParams.set('validate', 'true');
    const newSearch = searchParams.toString();

    navigateTo({
      search: newSearch,
    });
  };

  const toggleAuditMode = () => {
    const nextState = auditMode === 'true' ? 'false' : 'true';
    setAuditMode(nextState);
    return nextState;
  };

  const handleAuditReport = async (toggle = true) => {
    let next: 'true' | 'false' = 'false';
    if (toggle) {
      next = toggleAuditMode();
    }
    if (!toggle || next === 'true') {
      await getAuditReport(selectedReport?.id as string);
    } else {
      useReportsStore.setState({ auditReport: null });
    }
  };

  useEffect(() => {
    if (reportConfiguration) {
      setReportsConfiguration(reportConfiguration as ReportConfiguration);
    }
  }, [reportConfiguration]);

  useEffect(() => {
    if (auditMode === 'true') {
      handleAuditReport(false);
    }
    () => {
      useActiveReportStore.setState({ currentReport: undefined });
    };
  }, []);

  useEffect(() => {
    if (validation) {
      setShowValidationPane(true);
    }
  }, [validation]);

  const handleRefreshReport = async (e: SyntheticEvent<HTMLButtonElement>) => {
    try {
      const target = e.currentTarget;
      // allow only every two minutes

      const selectedReportId = selectedReport?.id;
      const updatedAt = currentReport?.updatedAt ? currentReport?.updatedAt : selectedReport?.updatedAt;
      const difference = updatedAt ? differenceInSeconds(new Date(), parseJSON(updatedAt)) : 0;

      const refreshTimeAllowed = 60 * 5;
      if (difference < refreshTimeAllowed) {
        showToast(`Report can be refreshed again in ${refreshTimeAllowed - difference} seconds`, TOAST_ERROR);
        return;
      }

      if (target) {
        target.disabled = true;
      }

      if (!selectedReportId) {
        return;
      }
      setRefreshingReport(true);
      const periodStartEndDates = currentReport?.dates;
      const extractedReportIds = extractReportIds(periodStartEndDates!);

      const reportIds = extractedReportIds.length > 0 ? extractedReportIds : [selectedReportId];
      await performReportUpdate(selectedReport!, reportIds);

      if (target) {
        target.disabled = false;
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      toast.error('Error refreshing report');
    } finally {
      setRefreshingReport(false);
    }
  };

  const reportDates = useMemo(() => {
    if (!dateRange) {
      return currentReport && currentReport.dates;
    }
    if (total || difference) {
      return (currentReport as any).dates;
    }
    const tempReportDates = [...currentReport!.dates];
    tempReportDates.pop();
    return tempReportDates;
  }, [new Date()]);

  const reportDatesHeaders = useMemo(() => {
    if (!reportDates) return;
    const reportDatesHeaderFormatted = reportDates.map((date: any) => {
      const hasSubheader =
        (date.type && date.type === ReportColumnTypes.Data) || date.type === ReportColumnTypes.Budget;

      const subheaderLabel = hasSubheader && date.type === ReportColumnTypes.Data ? 'ACTUAL' : 'BUDGET';

      const headerFormatted = getHeaderFormattedText(date, currentReport as NestedHoneReport, yearPlotted, false);

      const getPrintedDate = getPrintedHeaderDate(headerFormatted, null, false);

      return (getPrintedDate as string).concat(hasSubheader ? ` ${subheaderLabel}` : '');
    });
    return reportDatesHeaderFormatted;
  }, [reportDates]);

  const getReportChildren = (reports: NestedHoneReportSection[], flatArray: Report[] = []): Report[] => {
    for (const report of reports) {
      const maskedTitle = Array(report.level).fill(' * ').join('') + report.title;
      const maskedPercentageTitle = Array(report.level).fill(' * ').join('') + '% OF ' + report.title;

      flatArray.push({
        ...report,
        ...report.data?.map(data => (showMoneyValues ? currencyFormatter2Decimals(data?.amount) : '')),
        title: maskedTitle,
      });

      if (report.title !== '' && showPercentageValues) {
        flatArray.push({
          ...report,
          ...report.data?.map(data => percentageFormatter(data?.perc || 0)),
          title: maskedPercentageTitle,
        });
      }

      if (report.children) {
        getReportChildren(report.children, flatArray);
      }
    }
    return flatArray;
  };

  useEffect(() => {
    if (!isYtd) return;
    if (!selectedReport) return;
    const yearToPlot = Number(selectedReport.startDateText.split('-')[0]);
    setYearPlotted(yearToPlot);
  }, [selectedReport]);

  const validateParsed = urlParams.get('validate');

  useEffect(() => {
    if (isBalanceSheetPreset && reportConfiguration) {
      const type = ReportTypes.BalanceSheet;
      const preset = findBSPresetDates(
        BALANCE_SHEET_PRESETS[reportId as keyof typeof BALANCE_SHEET_PRESETS],
        reportId as BalanceSheetPreset,
        currentLocation?.weekStart,
        (reportConfiguration as ReportConfiguration)?.data
      );
      const startDate = preset.start;
      const endDate = preset.end;

      (async () => {
        const reportPayload: ReportRequest = {
          type,
          locations: [String(currentLocationId) || ''],
          templateId: templateId,
          startDate,
          endDate,
        };
        setReportPayload(reportPayload);

        const response = await getGroupReport(reportPayload);

        if (response === 'error') {
          navigateTo(`/app/location/${currentLocationId}`, { replace: true });
        }
      })();
    }
  }, [isBalanceSheetPreset, reportId, reportConfiguration]);

  useEffect(() => {
    if (!validateParsed) return;
    (async () => {
      const parsedReportId = selectedReport && selectedReport?.id;
      useActiveReportStore.setState({ status: 'loading', refreshingReport: false });
      const report = allReports.find(report => report.id === parsedReportId);
      const response = fetchReportById(report);
      if (validateParsed) {
        await toast.promise(
          response,
          {
            loading: 'Validating report, will take a few seconds...',
            success: 'Report, successfully validated',
            error: 'Error validating report',
          },
          {
            style: {
              maxWidth: 'max(50vw, 350px)',
            },
          }
        );
      }
    })();
  }, [validateParsed]);

  useEffect(() => {
    if (!allReports.length) return;
    let selectedReport: HoneReportSummary | undefined;
    if (reportType) {
      if (reportType === 'balance-sheet' && typeof reportId === 'undefined') {
        navigateTo(`/app/location/${locationId}/report/balance-sheet/6-months`, { replace: true });
        return;
      }

      selectedReport = reports.get(reportType)![0];
      if (prevReportType !== reportType) {
        setYearPlotted(new Date().getFullYear());
      }

      if (reportId) {
        if (Object.keys(BALANCE_SHEET_PRESETS).includes(reportId)) {
          useReportsStore.setState({ selectedReport });
          return;
        }

        const parsedReportId = reportId.includes('?') ? reportId.split('?')[0] : reportId;
        if (templateId && !dateRange) {
          selectedReport = reports.get(reportType)!.find(report => {
            return report.id === parsedReportId && report.templateId === templateId;
          });
        } else {
          const reportFound = reports.get(reportType)!.find(report => report.id === parsedReportId);

          if (!dateRange && !reportFound) {
            Sentry.captureException(`Report not found ${reportId}`);
            setTimeout(() => {
              showToast(
                "The report you are trying to access isn't available. Please click on another report to access the data you want.",
                TOAST_ERROR
              );
            }, 1);

            return;
          }
          selectedReport = reportFound;
        }
      } else {
        if (titleFilter && !dateRange) {
          selectedReport = reports
            .get(reportType)!
            .find(report => filteredTitleFilter === reportTitleToUrl(report.title));
        }
      }
      if (!selectedReport && !dateRange) {
        try {
          setTimeout(() => {
            showToast(
              "The report you are trying to access isn't available. Please click on another report to access the data you want.",
              TOAST_ERROR,
              FIVE_SECONDS
            );
          }, 1);

          const lookingForId = reportId || titleFilter;
          throw new ReportNotFoundError(lookingForId);
        } catch (error) {
          Sentry.captureException(error);
          navigateTo(`/app/location/${currentLocationId}`);
        }
      }

      if (useReportsStore.getState().selectedReport?.id !== selectedReport?.id) {
        useReportsStore.setState({ selectedReport });
      }
      if (selectedReport?.id) {
        useActiveReportStore.setState({ currentReportId: selectedReport.id });
      }
    }
  }, [allReports, reports, reportId, reportType]);

  const years = useMemo(
    () =>
      allReports.map(report => ({
        period: new Date(report.endDate).getFullYear(),
        reportId: report.id,
      })),
    [allReports]
  );

  const handleReportChange = (newReportId: string) => {
    if (newReportId === 'custom_range') {
      setOpenDateRangeModal(true);
      return;
    }

    setStatus('loading');
    setDateRange(undefined);

    const yearPlotted = years.find(year => year.reportId === newReportId);
    yearPlotted?.period && setYearPlotted(yearPlotted?.period);

    const { dateRange, total, difference, ...queryParams } = qs.parse(location.search.replace('?', ''));
    const newQueries = { ...queryParams };

    if (templateId) {
      newQueries.templateId = templateId;
    }
    useReportsStore.setState({ selectedReport: undefined });
    useActiveReportStore.setState({ currentReport: undefined });
    navigateTo({
      pathname: `/app/location/${currentLocationId}/report/${reportType}/${newReportId}`,
      search: qs.stringify(newQueries),
    });
  };

  // Disable smoothing for Balance Sheet and Cash Flow
  useEffect(() => {
    if (selectedReport && selectedReport?.type === HoneReportTypes.IncomeStatement)
      useReportGraphStore.setState({ extraReportId: undefined });
    if (
      selectedReport &&
      (selectedReport.type === HoneReportTypes.BalanceSheet || selectedReport.type === HoneReportTypes.CashFlow)
    ) {
      useActiveReportStore.setState({ smoothingEnabled: false });
    }
  }, [selectedReport]);

  const hasCharts =
    !isEmpty(allCategories) && selectedReport && ['P&L Comparison', 'Income Statement'].includes(selectedReport?.type);

  const showCharts = chartCollapsed === 'false' && auditMode !== 'true' && hasCharts && reportType !== 'ap-aging';

  const getFilteredReportsOptions = () => {
    if (selectedReport?.type === HoneReportTypes.BalanceSheet) {
      const startDateText = '',
        endDateText = '';
      const bsPresetReports = Object.entries(BALANCE_SHEET_PRESETS).map(([id, preset]) =>
        Object.assign(preset, { id, startDateText, endDateText })
      );
      return [...bsPresetReports, ...filteredReports];
    }

    return filteredReports;
  };
  const summaryReport = useReportGraphStore(state => state.summaryReport);

  const graphWidth = useMemo(() => {
    if (isYtd) {
      return 1200;
    }

    return 60 + 175 * ((summaryReport?.reportLabels || []).length ?? 3);
  }, [isYtd, selectedReport?.id, summaryReport?.reportLabels]);

  const filteredReportsOptions = getFilteredReportsOptions();

  const getSelectedReportId = useCallback(() => {
    if (reportId) return reportId.includes('?') ? reportId.split('?')[0] : reportId;
    if (dateRange) return 'custom_range';

    if (status === 'pending') return 'loading';

    if (selectedReport?.type === HoneReportTypes.PLComparison) {
      // check if index exists in array of reports
      return filteredReportsOptions[0].id;
    }

    const index = filteredReportsOptions.findIndex(report => report.id === selectedReport?.id);
    if (index > -1) {
      return filteredReportsOptions[index].id;
    }
    return filteredReportsOptions[0].id;
  }, [selectedReport]);

  if (reportsError) {
    return (
      <div className="report-view add-padding">
        <div className="report-view--header">
          <div />
          <div className="report-view--header__location"></div>
        </div>
        <div className="report-view-container h-100 center">{reportsError}</div>
      </div>
    );
  }
  if (reportStatus === 'loading') {
    return (
      <div className="h-100">
        <Loading />
      </div>
    );
  }

  const showAllButton =
    selectedReport?.type === HoneReportTypes.PLComparison && selectedReport?.timeframe === HoneReportTimeframes.Weekly;

  const canReadBookkeeperControls = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  const Icon: any = selectedReport && selectedReport.type in MENU_ICONS ? MENU_ICONS[selectedReport.type] : null;
  const rTitle: string = selectedReport && typeof selectedReport.type === 'string' ? selectedReport.type : 'P&L';

  const adminTools: ActionProps[] = [
    {
      handleClick: handleValidateReport,
      tooltip: 'Validate report',
      icon: <IconValidate />,
      placement: 'top',
    },
  ];

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="report-view">
        <KSHeader />
        <div className="report-view--header add-padding">
          <div className="report-view--header__item">
            <span className="header-icon header-icon-bottom ">{Icon && <Icon />}</span>
            <div className="report-view-header-title">{rTitle}</div>

            {reportType !== 'ap-aging' ? (
              <ReportDatesModal
                key={dateRange}
                uniqueTitles={uniqueTitles}
                timeframe={selectedReport && selectedReport.timeframe}
                selectedReportTitle={selectedReport ? selectedReport.title : ''}
                onLoadMore={() => setAllReportsSelected('true')}
                selectedOption={getSelectedReportId()}
                onReportChange={handleReportChange}
                options={filteredReportsOptions}
              />
            ) : (
              <div
                className={classNames('reports-selector-container', {
                  hovered: isHovered,
                })}
              >
                <select value={getSelectedReportId()} onChange={e => handleReportChange(e.currentTarget.value)}>
                  {filteredReportsOptions.length > 0 ? (
                    filteredReportsOptions.map(({ id, startDateText, endDateText }) => {
                      return (
                        <option value={id} key={id}>
                          {id === 'custom_range' &&
                            `Custom Range ${dateRange ? '[' + dateRange?.split('&')[0] + ']' : ''} `}
                          {Object.keys(BALANCE_SHEET_PRESETS).includes(id)
                            ? BALANCE_SHEET_PRESETS[id as keyof typeof BALANCE_SHEET_PRESETS].name
                            : !['custom_range'].includes(id) &&
                              `${formatDate(new Date(startDateText + 'T00:00:00'))} - ${formatDate(
                                new Date(endDateText + 'T00:00:00')
                              )}`}
                        </option>
                      );
                    })
                  ) : (
                    <option value="loading">Loading...</option>
                  )}
                </select>
                {showAllButton && (
                  <button
                    disabled={allReportsSelected}
                    className="btn btn-secondary btn-secondary-small"
                    onClick={() => setAllReportsSelected('true')}
                  >
                    All
                  </button>
                )}
                <ModalCustomDateRange uniqueTitles={uniqueTitles} />
              </div>
            )}
          </div>
          <div className="report-view--header__item">
            <div>
              {canReadBookkeeperControls && selectedReport && (
                <STBTools
                  reportHeaders={reportDatesHeaders}
                  adminMode={isBookkeeper}
                  status={(statusUpdates[selectedReport.id] || selectedReport.status) as HoneReportStatus}
                  onRefresh={(e: SyntheticEvent<HTMLButtonElement, Event>) => handleRefreshReport(e)}
                  onDownload={() => {}}
                  adminTools={adminTools}
                  report={currentReport}
                />
              )}
            </div>
          </div>
        </div>
        {selectedReport && (
          <div className="report-view-container">
            {!newTable ? (
              <>
                {showCharts && (
                  <ReportGraph
                    graphWidth={graphWidth}
                    isYTD={isYtd}
                    selectedReport={selectedReport}
                    reports={filteredReports}
                    extraReportDataSummary={null}
                    timeframe={selectedReport.timeframe}
                    onReportChange={handleReportChange}
                  />
                )}
                <Suspense
                  fallback={
                    <div className="h-100 center">
                      Loading No PL...
                      <Loading />
                    </div>
                  }
                >
                  {selectedReport.format === HoneReportFormats.PDF && <PDFObject url={selectedReport.dataLoc} />}
                  {selectedReport.format === HoneReportFormats.JSON && (
                    <ReportDataTable
                      report={selectedReport}
                      columnHeaderTitle={columnHeaderTitle}
                      customHeaders={customHeaders || []}
                      hasCharts={!!showCharts}
                      selectedReportType={selectedReport.type}
                    />
                  )}
                </Suspense>
              </>
            ) : (
              <>
                {selectedReport.format === HoneReportFormats.JSON && (
                  <ReportDataTable
                    report={selectedReport}
                    columnHeaderTitle={columnHeaderTitle}
                    customHeaders={customHeaders || []}
                    hasCharts={!!showCharts}
                    selectedReportType={selectedReport.type}
                    newTable={newTable}
                  />
                )}
              </>
            )}
          </div>
        )}
        <SlidingPane
          onRequestClose={toggleValidate}
          title={`Validation results`}
          width={'75%'}
          isOpen={showValidationPane}
        >
          {validateReportError && <span>{validateReportError}</span>}
          {validationReportData && <ValidationReportResults reportData={validationReportData} />}
        </SlidingPane>
      </div>
    </ErrorBoundary>
  );
}

export default ReportViewNoPl;
